// NoblePullToRefresh - Pull-to-refresh for mobile data views
// Pull down to trigger refresh/sync
// Features: Elastic animation, loading spinner, customizable threshold

import React, { useState, useRef, useEffect } from 'react';
import { useDrag } from '@use-gesture/react';
import { useSpring, animated, config } from '@react-spring/web';
import styled from '@emotion/styled';
import { Spin } from 'antd';
import { LoadingOutlined, DownOutlined, SyncOutlined } from '@ant-design/icons';
import { NobleColors, NobleSpacing } from '../../../theme/noble-tokens';

export interface NoblePullToRefreshProps {
  children: React.ReactNode;
  onRefresh: () => Promise<void>;
  refreshThreshold?: number; // px to trigger refresh
  maxPullDistance?: number; // maximum pull distance
  disabled?: boolean;
  pullingText?: string;
  releaseText?: string;
  refreshingText?: string;
  className?: string;
}

// Styled Components
const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  touch-action: pan-y; // Allow vertical pan
  -webkit-overflow-scrolling: touch;
`;

const RefreshIndicator = styled(animated.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${NobleSpacing.xs};
  background: linear-gradient(180deg, ${NobleColors['level-0-light']} 0%, transparent 100%);
  z-index: 100;
  transform: translateY(-100%);
  will-change: transform;
`;

const RefreshIcon = styled(animated.div)`
  font-size: 24px;
  color: ${NobleColors['command-blue-base']};
`;

const RefreshText = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${NobleColors['steel-gray-base']};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ContentWrapper = styled(animated.div)`
  width: 100%;
  height: 100%;
  will-change: transform;
  position: relative;
`;

// Main Component
export const NoblePullToRefresh: React.FC<NoblePullToRefreshProps> = ({
  children,
  onRefresh,
  refreshThreshold = 80, // px
  maxPullDistance = 120, // px
  disabled = false,
  pullingText = 'Pull to refresh',
  releaseText = 'Release to sync',
  refreshingText = 'Syncing...',
  className,
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullState, setPullState] = useState<'idle' | 'pulling' | 'release' | 'refreshing'>('idle');
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollTop = useRef(0);

  // Spring for content position
  const [{ y }, api] = useSpring(() => ({
    y: 0,
    config: config.stiff,
  }));

  // Spring for indicator
  const [{ indicatorY, indicatorOpacity, iconRotation }, indicatorApi] = useSpring(() => ({
    indicatorY: -80,
    indicatorOpacity: 0,
    iconRotation: 0,
    config: config.gentle,
  }));

  // Check if user is at top of scroll container
  const isAtTop = () => {
    const container = containerRef.current;
    if (!container) return true;
    
    // Find scrollable child
    const scrollableChild = container.querySelector('[data-scrollable]') || container.firstElementChild;
    if (!scrollableChild) return true;
    
    const scrollTop = scrollableChild.scrollTop || 0;
    return scrollTop <= 0;
  };

  // Drag gesture
  const bind = useDrag(
    async ({ down, movement: [, my], velocity: [, vy], direction: [, dy], cancel, first, last }) => {
      if (disabled || isRefreshing) return;

      // Only allow pull-down when at top
      if (first && !isAtTop()) {
        cancel();
        return;
      }

      // Only handle downward pull
      if (my < 0) {
        cancel();
        return;
      }

      const progress = Math.min(my / refreshThreshold, 1);
      const shouldTrigger = my > refreshThreshold;

      // Update pull state
      if (down) {
        setPullState(shouldTrigger ? 'release' : 'pulling');
      }

      // Animate content and indicator while pulling
      if (down && my > 0) {
        // Apply resistance curve
        const resistedY = Math.min(my * 0.5, maxPullDistance);
        
        api.start({
          y: resistedY,
          immediate: true,
        });

        indicatorApi.start({
          indicatorY: resistedY - 80,
          indicatorOpacity: progress,
          iconRotation: shouldTrigger ? 180 : progress * 180,
          immediate: true,
        });
      }

      // Release
      if (last) {
        if (shouldTrigger && !isRefreshing) {
          // Trigger refresh
          setPullState('refreshing');
          setIsRefreshing(true);

          // Keep indicator visible during refresh
          api.start({ y: 80, config: config.wobbly });
          indicatorApi.start({
            indicatorY: 0,
            indicatorOpacity: 1,
            iconRotation: 0,
          });

          try {
            // Haptic feedback
            if ('vibrate' in navigator) {
              navigator.vibrate(20);
            }

            await onRefresh();
          } finally {
            // Reset after refresh completes
            setIsRefreshing(false);
            setPullState('idle');
            
            api.start({ y: 0, config: config.wobbly });
            indicatorApi.start({
              indicatorY: -80,
              indicatorOpacity: 0,
              iconRotation: 0,
              config: config.slow,
            });
          }
        } else {
          // Snap back if not triggered
          setPullState('idle');
          api.start({ y: 0, config: config.wobbly });
          indicatorApi.start({
            indicatorY: -80,
            indicatorOpacity: 0,
            iconRotation: 0,
          });
        }
      }
    },
    {
      axis: 'y',
      filterTaps: true,
      rubberband: true,
    }
  );

  // Render refresh icon based on state
  const renderRefreshIcon = () => {
    if (isRefreshing) {
      return <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />;
    }

    if (pullState === 'release') {
      return <SyncOutlined spin={false} />;
    }

    return (
      <animated.div style={{ transform: iconRotation.to((r) => `rotate(${r}deg)`) }}>
        <DownOutlined />
      </animated.div>
    );
  };

  // Render text based on state
  const renderRefreshText = () => {
    if (isRefreshing) return refreshingText;
    if (pullState === 'release') return releaseText;
    return pullingText;
  };

  return (
    <Container ref={containerRef} className={className} {...bind()}>
      {/* Refresh Indicator */}
      <RefreshIndicator
        style={{
          transform: indicatorY.to((y) => `translateY(${y}px)`),
          opacity: indicatorOpacity,
        }}
      >
        <RefreshIcon>{renderRefreshIcon()}</RefreshIcon>
        <RefreshText>{renderRefreshText()}</RefreshText>
      </RefreshIndicator>

      {/* Content */}
      <ContentWrapper style={{ transform: y.to((y) => `translateY(${y}px)`) }}>
        {children}
      </ContentWrapper>
    </Container>
  );
};

// Wrapper component for scrollable lists
interface NoblePullToRefreshListProps extends NoblePullToRefreshProps {
  height?: string | number;
}

export const NoblePullToRefreshList: React.FC<NoblePullToRefreshListProps> = ({
  children,
  height = '100%',
  ...props
}) => {
  return (
    <NoblePullToRefresh {...props}>
      <div
        data-scrollable
        style={{
          height: typeof height === 'number' ? `${height}px` : height,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {children}
      </div>
    </NoblePullToRefresh>
  );
};

